import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import useBottomBoundedViewport from '../utils/useBottomBoundedViewport';
import useBodyScrollLock from '../utils/useBodyScrollLock';
import '../styles/Events.css';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const STATUS_LABELS = {
  upcoming: 'Upcoming',
  ongoing: 'Ongoing',
  past: 'Past'
};

const Events = ({ lang }) => {
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');
  const bbv = useBottomBoundedViewport({ topOffset: 90, bottomGap: 16 });
  // Lock background scroll while the detail modal is open so the user
  // can only scroll the article they've opened.
  useBodyScrollLock(!!selected);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await fetch('/api/events');
        const data = await res.json();
        setEvents(data.events || []);
      } catch { setEvents([]); }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const filtered = filter === 'all' ? events : events.filter(e => e.status === filter);

  return (
    <section className="events section" id="events" ref={ref}>
      <div className="container">
        <motion.div
          className="events-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="terminal-section-header">
                <div className="terminal-section-titlebar">
                  root@ispclub:~/# cd /ispclub/events
                </div>
                <div className="terminal-section-body">
                  <span className="terminal-sh-line">&gt;_ sh events.sh</span>
                </div>
              </div>
          <h2 className="section-title">{lang.EVENTS_TITLE || 'ISP Club Events'}</h2>
          <p className="section-subtitle">
            {lang.EVENTS_SUBTITLE || 'Workshops, CTFs, seminars and meetups organized by ISP Club.'}
          </p>
        </motion.div>

        <div className="events-filters">
          {[
            { k: 'all', label: 'All' },
            { k: 'upcoming', label: STATUS_LABELS.upcoming },
            { k: 'ongoing', label: STATUS_LABELS.ongoing },
            { k: 'past', label: STATUS_LABELS.past }
          ].map(f => (
            <button
              key={f.k}
              className={`events-filter-btn ${filter === f.k ? 'active' : ''}`}
              onClick={() => setFilter(f.k)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="events-empty">
            {lang.EVENTS_EMPTY || 'No events in this category yet.'}
          </p>
        )}

        <div className="events-grid">
          {filtered.map((ev, index) => (
            <motion.article
              key={ev.id}
              className={`event-card status-${ev.status || 'upcoming'}`}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 * index, duration: 0.5 }}
              whileHover={{ y: -6 }}
              onClick={() => setSelected(ev)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') setSelected(ev); }}
            >
              {ev.coverImage ? (
                <div className="event-cover">
                  <img src={`/uploads/events/${ev.coverImage}`} alt={ev.title} />
                  <span className={`event-status-badge status-${ev.status || 'upcoming'}`}>
                    {STATUS_LABELS[ev.status] || STATUS_LABELS.upcoming}
                  </span>
                </div>
              ) : (
                <div className="event-cover event-cover-placeholder">
                  <span>📅</span>
                  <span className={`event-status-badge status-${ev.status || 'upcoming'}`}>
                    {STATUS_LABELS[ev.status] || STATUS_LABELS.upcoming}
                  </span>
                </div>
              )}
              <div className="event-body">
                <h3 className="event-title">{ev.title}</h3>
                {ev.eventDate && (
                  <div className="event-meta-row">
                    <span>🗓 {formatDate(ev.eventDate)}</span>
                    {ev.location && <span>📍 {ev.location}</span>}
                  </div>
                )}
                {ev.excerpt && <p className="event-excerpt">{ev.excerpt}</p>}
                <span className="event-read-more">
                  {lang.EVENTS_READ_MORE || 'View Details'}
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </div>
            </motion.article>
          ))}
        </div>

        <AnimatePresence>
          {selected && (
            <motion.div
              className="blog-modal-overlay blog-modal-overlay-bounded"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              style={{ top: bbv.topOffset, bottom: bbv.bottom }}
              onClick={() => setSelected(null)}
            >
              <motion.div
                className="blog-modal event-modal"
                initial={{ opacity: 0, y: 40, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 40, scale: 0.97 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                onClick={(e) => e.stopPropagation()}
              >
                <button className="blog-modal-close" onClick={() => setSelected(null)} aria-label={lang.COMMON_CLOSE || 'Close'}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>

                {selected.coverImage && (
                  <div className="event-modal-cover">
                    <img src={`/uploads/events/${selected.coverImage}`} alt={selected.title} />
                  </div>
                )}

                <div className="event-modal-header">
                  <span className={`event-status-badge status-${selected.status || 'upcoming'}`}>
                    {STATUS_LABELS[selected.status] || STATUS_LABELS.upcoming}
                  </span>
                  {selected.eventDate && <span className="blog-date">🗓 {formatDate(selected.eventDate)}</span>}
                  {selected.location && <span className="blog-date">📍 {selected.location}</span>}
                </div>

                <h1 className="blog-modal-title">{selected.title}</h1>

                <div className="blog-modal-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {selected.content || ''}
                  </ReactMarkdown>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Events;
